import { match } from "ts-pattern";

import { IncidentTypeSchema } from "../../types/incidentType.js";
import type { ActionInput } from "../ActionInput.js";
import { getSupportedIncidentTypeSelection } from "./getIncidentTypeSelection.js";

/**
 * Gets the appropriate i18next translation context based on the incident's
 * triage selection (whatWillYouDo field).
 *
 * This context is used to display workflow-specific terminology:
 * - "136" context: Uses "Place of Safety" / "POS" terminology
 * - "mca" context: Uses "Emergency Department" / "ED" terminology
 *
 * Falls back to "136" for voluntary/other workflows until those are fully defined.
 *
 * @param incidentWorkItem - The incident work item containing triage form data
 * @returns The workflow translation context value ("136" or "mca")
 *
 * @example
 * // In a component or server handler:
 * const context = getWorkflowTranslationContext({ incidentWorkItem });
 * const label = t("recordPlaceOfSafety.title", { context });
 * // For 136: "Record Place of Safety"
 * // For MCA: "Record Emergency Department"
 */
export function getWorkflowTranslationContext({
  incidentWorkItem,
}: Partial<Pick<ActionInput, "incidentWorkItem">>):
  | WorkflowTranslationContextValue
  | undefined {
  return incidentWorkItem
    ? match(
        getSupportedIncidentTypeSelection({ incidentWorkItem })
          // .orTee((e) => {
          //   console.error("Unsupported incident type", e.prettifyError());
          // })
          .unwrapOr(undefined),
      )
        .with(
          IncidentTypeSchema.enum.mentalCapacityAct,
          () => WorkflowTranslationContext.mentalCapacityAct,
        )
        .with(
          IncidentTypeSchema.enum.section136,
          () => WorkflowTranslationContext.section136,
        )
        .with(undefined, () => WorkflowTranslationContext.section136)
        .exhaustive()
    : undefined;
}

/**
 * The workflow translation context values used with i18next's context feature.
 * These are used as suffixes for translation keys to provide workflow-specific
 * terminology (e.g., "Place of Safety" for 136 vs "Emergency Department" for MCA).
 */
export const WorkflowTranslationContext = {
  section136: "136",
  mentalCapacityAct: "mca",
} as const;

export type WorkflowTranslationContextValue =
  (typeof WorkflowTranslationContext)[keyof typeof WorkflowTranslationContext];
